// Full-state hash (IMPLEMENTATION §0.1): FNV-1a (32-bit) over the canonical JSON of the whole SimState.
// Canonical = object keys in ordinal (UTF-16 code unit) order, no whitespace, integers only. Nothing is
// excluded because nothing is a float: a non-integer number, NaN, undefined or a function THROWS, so a
// float leaking into the state fails the first hash instead of silently diverging between engines.
// C# port: sort keys with string.CompareOrdinal, hash the UTF-8 bytes, `unchecked` uint multiply.
import type { Int } from './types.ts';

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

function fnvByte(h: Int, b: Int): Int {
  return Math.imul(h ^ b, FNV_PRIME) >>> 0;
}

/** FNV-1a 32 over the UTF-8 encoding of `str`. */
export function fnv1a32(str: string): Int {
  let h = FNV_OFFSET;
  for (let i = 0; i < str.length; i++) {
    let c = str.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < str.length) {
      const lo = str.charCodeAt(i + 1);
      if (lo >= 0xdc00 && lo <= 0xdfff) {
        c = 0x10000 + ((c - 0xd800) << 10) + (lo - 0xdc00);
        i++;
      }
    }
    if (c < 0x80) {
      h = fnvByte(h, c);
    } else if (c < 0x800) {
      h = fnvByte(h, 0xc0 | (c >>> 6));
      h = fnvByte(h, 0x80 | (c & 0x3f));
    } else if (c < 0x10000) {
      h = fnvByte(h, 0xe0 | (c >>> 12));
      h = fnvByte(h, 0x80 | ((c >>> 6) & 0x3f));
      h = fnvByte(h, 0x80 | (c & 0x3f));
    } else {
      h = fnvByte(h, 0xf0 | (c >>> 18));
      h = fnvByte(h, 0x80 | ((c >>> 12) & 0x3f));
      h = fnvByte(h, 0x80 | ((c >>> 6) & 0x3f));
      h = fnvByte(h, 0x80 | (c & 0x3f));
    }
  }
  return h;
}

/** Keys in ordinal order (insertion sort: no engine sort, no locale). */
function sortedKeys(o: object): string[] {
  const keys = Object.keys(o);
  for (let i = 1; i < keys.length; i++) {
    const k = keys[i] as string;
    let j = i - 1;
    while (j >= 0 && (keys[j] as string) > k) {
      keys[j + 1] = keys[j] as string;
      j--;
    }
    keys[j + 1] = k;
  }
  return keys;
}

function write(v: unknown, out: string[], path: string): void {
  if (v === null) {
    out.push('null');
    return;
  }
  switch (typeof v) {
    case 'boolean':
      out.push(v ? 'true' : 'false');
      return;
    case 'number':
      if (!Number.isSafeInteger(v)) {
        throw new TypeError(`canonicalJson: non-integer ${v} at ${path}`);
      }
      out.push(String(v));
      return;
    case 'string':
      out.push(JSON.stringify(v));
      return;
    case 'object': {
      if (Array.isArray(v)) {
        out.push('[');
        for (let i = 0; i < v.length; i++) {
          if (i > 0) out.push(',');
          write(v[i], out, `${path}[${i}]`);
        }
        out.push(']');
        return;
      }
      const rec = v as Record<string, unknown>;
      out.push('{');
      let first = true;
      for (const k of sortedKeys(rec)) {
        if (!first) out.push(',');
        first = false;
        out.push(JSON.stringify(k), ':');
        write(rec[k], out, `${path}.${k}`);
      }
      out.push('}');
      return;
    }
    default:
      throw new TypeError(`canonicalJson: unsupported ${typeof v} at ${path}`);
  }
}

/** Canonical JSON: sorted keys, no whitespace; throws on floats, NaN, undefined and functions. */
export function canonicalJson(v: unknown): string {
  const out: string[] = [];
  write(v, out, '$');
  return out.join('');
}

/** 8 lowercase hex digits. */
export function hex8(h: Int): string {
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** FNV-1a of the canonical JSON, as 8 hex digits. */
export function hashValue(v: unknown): string {
  return hex8(fnv1a32(canonicalJson(v)));
}
